export enum CategoryNodeType {
    BRANCH = "BRANCH",
    LEAF   = "LEAF",
}

/**
 * Lightweight view of any node in the category tree (branch or leaf), as
 * returned by the roots / children / subtree endpoints. A leaf additionally
 * carries parameters (see {@link Category}).
 */
export interface CategoryNodeSummary {
    id: string;
    version?: number;
    name: string;
    description?: string;
    parentId?: string | null;
    ancestorIds: string[];
    depth: number;
    globallyUnique: boolean;
    hasServiceArea?: boolean;
    /** Listings sit at a fixed location (rentals, real estate); search
     *  matches the listing's own location, not the store's. */
    hasItemLocation?: boolean;
    nodeType: CategoryNodeType;
    similarNames?: string[];
}

export interface Category {
    id: string;
    version?: number;
    name: string;
    description: string;
    parameters: CategoryParameter[];

    // Hierarchy fields (present when the category is read as a tree node / leaf).
    parentId?: string | null;
    ancestorIds?: string[];
    depth?: number;
    globallyUnique?: boolean;
    hasServiceArea?: boolean;
    hasItemLocation?: boolean;
    nodeType?: CategoryNodeType;

    // Author-supplied synonyms/aliases that help users find the category.
    similarNames?: string[];
  }

export interface CategoryBranchCreationRequest {
    name: string;
    description?: string;
    parentId?: string | null;
    globallyUnique?: boolean;
    hasServiceArea?: boolean;
    hasItemLocation?: boolean;
    similarNames?: string[];
}

export interface PatchCategoryAddSimilarNameDTO {
    id: string;
    version: number;
    patchTask: "AddSimilarName";
    similarName: string;
}

export interface PatchCategoryRemoveSimilarNameDTO {
    id: string;
    version: number;
    patchTask: "RemoveSimilarName";
    similarName: string;
}

export interface PatchCategoryMoveDTO {
    id: string;
    version: number;
    patchTask: "MoveCategory";
    newParentId: string | null;
}

export enum ParameterDataType {
    BOOLEAN = "BOOLEAN",
    STRING  = "STRING",
    INTEGER = "INTEGER",
    DOUBLE  = "DOUBLE",
    SELECTABLE = "SELECTABLE",
    /** Calendar date (no time), ISO yyyy-MM-dd. */
    DATE = "DATE",
}

export enum SelectionType {
    SINGLE = "SINGLE",
    MULTI  = "MULTI",
}

export interface CategoryParameter {
    id: number;
    name: string;
    description?: string;
    dataType: ParameterDataType;

    // SELECTABLE only
    selectionType?: SelectionType;
    options?: string[];

    // INTEGER: optional single unit.
    unitId?: string;
    // DOUBLE: one or more allowed units (first is the primary/default).
    unitIds?: string[];
    // DOUBLE: false when the unit dimension is a currency (value stored only in
    // the entered unit; the specific currency is chosen at listing/search time).
    convertible?: boolean;
}


export type MetadataCategorySearchDTO = {
    id: string;
    name: string;
    description?: string;
    options?: string[]; // ✅ for SELECTABLE
};


export interface AddMetadataParameterDTO {
    name: string;
    description?: string;
    dataType: ParameterDataType;
    unitId?: string;
}

export interface MetadataParameterDTO {
    id: number;
    name: string;
    description?: string;
    dataType: ParameterDataType;
    unitId?: string;
}


export interface Selectable {
    id : number;
    name : string;
}

export interface MetadataParameterSelectableDTO extends MetadataParameterDTO{
    selectables: Selectable[];
    selectionType: SelectionType;
}



// The following classes had to be done different here from the Java
// MetadataParameterCreationRequestDTO and
// MetadataParameterSelectableCreationRequestDTO
export interface MetadataParameterCreationRequestBase {
    name: string;
    description?: string;
}

export interface MetadataParameterCreationRequestDTO
    extends MetadataParameterCreationRequestBase {
    dataType:
        | ParameterDataType.STRING
        | ParameterDataType.INTEGER
        | ParameterDataType.DOUBLE
        | ParameterDataType.BOOLEAN
        | ParameterDataType.DATE;
    // INTEGER: optional single unit.
    unitId?: string;
    // DOUBLE: one or more allowed units.
    unitIds?: string[];
}

export interface MetadataParameterSelectableCreationRequestDTO
    extends MetadataParameterCreationRequestBase {
    dataType: ParameterDataType.SELECTABLE;
    selectionType: SelectionType;
    selectables: string[];
}

export type MetadataCategoryParameterCreationRequestDTO =
    | MetadataParameterCreationRequestDTO
    | MetadataParameterSelectableCreationRequestDTO;

export type MetadataCategoryCreationRequestDTO = {
    name: string;
    description?: string;
    parentId?: string | null;
    globallyUnique?: boolean;
    hasServiceArea?: boolean;
    hasItemLocation?: boolean;
    parameters?: MetadataCategoryParameterCreationRequestDTO[];
    similarNames?: string[];
};

export interface PatchCategoryUpdateNameDTO {
    id: string;
    version: number;
    patchTask: "ChangeName";
    newName: string;
}

export interface PatchCategoryUpdateDescriptionDTO {
    id: string;
    version: number;
    patchTask: "ChangeDescription";
    newDescription: string;
}

export interface PatchCategoryAddParameterDTO {
    id: string;
    version: number;
    patchTask: "AddParameter";
    createParameterDTO: MetadataParameterCreationRequestDTO;
}

export interface PatchCategoryAddSelectableParameterDTO {
    id: string;
    version: number;
    patchTask: "AddSelectableParameter";
    createParameterDTO: MetadataParameterSelectableCreationRequestDTO;
}


export interface PatchCategoryRemoveParameterDTO {
    id: string;
    version: number;
    patchTask: "RemoveParameter";
    parameterId: number;
}


export interface PatchCategoryAddChoiceToSelectableParameterDTO {
    id: string;
    version: number;
    patchTask: "AddChoiceToSelectableParameter";
    parameterId: number;
    newSelectableChoice: string;
    /** 0-based insert position; omitted / out of range appends at the end. */
    position?: number;
}

export interface PatchCategoryChangeSelectableChoiceDTO {
    id: string;
    version: number;
    patchTask: "ChangeChoiceInSelectableParameter";
    parameterId: number;
    choiceId: number;
    newName: string;
}

export interface PatchCategoryRemoveSelectableChoiceDTO {
    id: string;
    version: number;
    patchTask: "RemoveChoiceFromSelectableParameter";
    parameterId: number;
    choiceId: number;
}

export interface PatchCategoryParameterUpdateNameDTO {
    id: string;
    version: number;
    patchTask: "ChangeParameterName";
    parameterId: number;
    newName: string;
}

export interface PatchCategoryParameterUpdateDescriptionDTO {
    id: string;
    version: number;
    patchTask: "ChangeParameterDescription";
    parameterId: number;
    newDescription: string;
}

export interface PatchCategoryChangeParameterUnitDTO {
    id: string;
    version: number;
    patchTask: "ChangeParameterUnit";
    parameterId: number;
    // Full replacement unit list. DOUBLE may hold several (one per system);
    // INTEGER holds zero or one.
    newUnitIds: string[];
}


